import { useEffect, useRef } from 'react';
import { DeliverymanRouteBox } from '@/_features/deliveries/componentes/deliveryman-route-box/deliveryman-route-box';
import { hasDeliverymanRouteContent } from '@/_features/deliveries/componentes/deliveryman-route-box/utils';
import { queryClient } from '@/_config/lib/react-query/query-client';
import { useMonitorDeliveryDetail } from '../../api/get-monitor-delivery-detail';
import {
  GET_MONITOR_DELIVERY_DETAIL,
  MONITOR_EXIT_STATUSES,
} from '../../constants';
import type { GetMonitorDeliveryDetailResponse } from '../../types';
import { RefreshCountdown } from '../refresh-countdown/refresh-countdown';
import { useMinutesSince } from '../../hooks/use-minutes-since';
import { toDeliveryRouteData } from '../../to-delivery-route-data';
import { formatMonitorAddress, projectMonitorDetailMetrics } from '../../utils';
import { DeliveryMetrics } from './components/delivery-metrics/delivery-metrics';
import { DeliverymanInfo } from './components/deliveryman-info/deliveryman-info';
import { DrawerFooter } from './components/drawer-footer/drawer-footer';
import { DrawerHeader } from './components/drawer-header/drawer-header';
import { DeliveryDetailErrorState } from './components/error-state/delivery-detail-error-state';
import { PlaceCustomerCards } from './components/place-customer-cards/place-customer-cards';
import { DeliveryDetailSkeleton } from './components/skeleton/delivery-detail-skeleton';
import styles from './delivery-detail-drawer.module.scss';

interface DeliveryDetailDrawerProps {
  deliveryId: string;
  open: boolean;
  onClose: () => void;
  onExited: () => void;
}

const hasModalOpen = () =>
  !!document.querySelector('[aria-modal="true"], [role="dialog"]');

export function DeliveryDetailDrawer({
  deliveryId,
  open,
  onClose,
  onExited,
}: DeliveryDetailDrawerProps) {
  const drawerRef = useRef<HTMLElement>(null);
  const {
    data: detail,
    dataUpdatedAt,
    errorUpdatedAt,
    isFetching,
    isLoading,
    isError,
    refetch,
  } = useMonitorDeliveryDetail(deliveryId);
  const minutesSinceUpdate = useMinutesSince(dataUpdatedAt);
  const lastUpdatedAt = Math.max(dataUpdatedAt, errorUpdatedAt);

  useEffect(() => {
    if (open) {
      drawerRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !hasModalOpen()) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  async function handleActionFinished() {
    const queryKey = [GET_MONITOR_DELIVERY_DETAIL, deliveryId];

    await queryClient.invalidateQueries(queryKey);

    const updatedDetail =
      queryClient.getQueryData<GetMonitorDeliveryDetailResponse>(queryKey);

    if (updatedDetail && MONITOR_EXIT_STATUSES.includes(updatedDetail.status)) {
      onClose();
    }
  }

  function renderContent() {
    if (isLoading) {
      return <DeliveryDetailSkeleton />;
    }

    if (isError || !detail) {
      return <DeliveryDetailErrorState onRetry={() => refetch()} />;
    }

    const routeData = toDeliveryRouteData(detail);

    return (
      <>
        {hasDeliverymanRouteContent(routeData) && (
          <DeliverymanRouteBox
            delivery={routeData}
            placeAddress={formatMonitorAddress(detail.place.address)}
            deliveryAddress={formatMonitorAddress(detail.client.address)}
            collapsibleSequence
          />
        )}

        <PlaceCustomerCards place={detail.place} client={detail.client} />

        {detail.deliveryman && detail.metrics && (
          <DeliveryMetrics
            status={detail.status}
            metrics={projectMonitorDetailMetrics(
              detail.metrics,
              minutesSinceUpdate,
            )}
          />
        )}

        {detail.deliveryman && (
          <DeliverymanInfo deliveryman={detail.deliveryman} />
        )}

        <DrawerFooter detail={detail} onActionFinished={handleActionFinished} />
      </>
    );
  }

  return (
    <>
      <div
        className={styles.backdrop}
        data-state={open ? 'open' : 'closed'}
        aria-hidden
        onClick={onClose}
      />

      <aside
        ref={drawerRef}
        className={styles.drawer}
        data-state={open ? 'open' : 'closed'}
        aria-label="Detalhe da entrega"
        tabIndex={-1}
        onAnimationEnd={event => {
          if (!open && event.target === event.currentTarget) {
            onExited();
          }
        }}
      >
        <div className={styles.header}>
          <DrawerHeader
            code={detail?.code}
            cityName={detail?.city.name}
            deliveryType={detail?.deliveryType}
            actions={
              lastUpdatedAt > 0 ? (
                <RefreshCountdown
                  lastUpdatedAt={lastUpdatedAt}
                  isRefreshing={isFetching}
                />
              ) : null
            }
            onClose={onClose}
          />
        </div>

        <div className={styles.content}>{renderContent()}</div>
      </aside>
    </>
  );
}
